import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Add from '@mui/icons-material/esm/Add';
import Refresh from '@mui/icons-material/esm/Refresh';
import Send from '@mui/icons-material/esm/Send';
import Visibility from '@mui/icons-material/esm/Visibility';
import { PageHeader } from '../../components/layout';
import { RootState } from '../../store';
import dfrService, { DailyFieldReport, DfrEntryPayload, DfrEntryType, DfrPayload, DfrStatus } from '../../services/api/dfrService';
import VoiceInput from '../../components/field/VoiceInput';
import { cachedRecords, enqueueOfflineDfr, mergeById } from '../../services/offline/fieldOfflineForms';

interface DfrPageProps {
  fieldMode?: boolean;
}

const today = () => new Date().toISOString().slice(0, 10);
const labelize = (value: string) => value.replace(/_/g, ' ');
const entryTypes: DfrEntryType[] = ['progress', 'delay', 'safety', 'visitor', 'materials', 'equipment'];

const newEntry = (): DfrEntryPayload => ({
  entryType: 'progress',
  description: '',
  taskId: '',
  documentId: '',
  fieldEvidenceId: '',
});

export default function DfrPage({ fieldMode = false }: DfrPageProps) {
  const { activeProjectId } = useSelector((state: RootState) => state.projects);
  const [rows, setRows] = useState<DailyFieldReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [status, setStatus] = useState<DfrStatus | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<DailyFieldReport | null>(null);
  const [form, setForm] = useState<DfrPayload>({
    projectId: activeProjectId ?? '',
    reportDate: today(),
    weather: '',
    temperatureC: null,
    manpowerCount: 0,
    workSummary: '',
    delaysConstraints: '',
    entries: [newEntry()],
  });

  const load = async () => {
    if (!activeProjectId) return;
    try {
      setLoading(true);
      setError(null);
      const cached = fieldMode ? await cachedRecords<DailyFieldReport>('daily_field_report', activeProjectId) : [];
      if (fieldMode && !navigator.onLine) {
        setRows(filterDfrRows(cached, status));
        return;
      }
      const serverRows = await dfrService.list({ projectId: activeProjectId, status });
      setRows(fieldMode ? filterDfrRows(mergeById(serverRows, cached), status) : serverRows);
    } catch (err: any) {
      if (fieldMode) {
        const cached = await cachedRecords<DailyFieldReport>('daily_field_report', activeProjectId);
        setRows(filterDfrRows(cached, status));
        setError(cached.length ? null : err.response?.data?.error || err.response?.data?.message || 'Failed to load DFRs');
      } else {
        setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load DFRs');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId, status]);

  useEffect(() => {
    setForm((current) => ({ ...current, projectId: activeProjectId ?? '' }));
  }, [activeProjectId]);

  const totals = useMemo(() => ({
    reports: rows.length,
    entries: rows.reduce((sum, row) => sum + (row.entries?.length ?? 0), 0),
  }), [rows]);

  const openCreate = () => {
    setForm({
      projectId: activeProjectId ?? '',
      reportDate: today(),
      weather: '',
      temperatureC: null,
      manpowerCount: 0,
      workSummary: '',
      delaysConstraints: '',
      entries: [newEntry()],
    });
    setDialogOpen(true);
  };

  const save = async (submitAfter = false) => {
    const payload = {
      ...form,
      temperatureC: form.temperatureC === null || form.temperatureC === undefined ? null : Number(form.temperatureC),
      manpowerCount: Number(form.manpowerCount ?? 0),
      entries: (form.entries ?? []).filter((entry) => entry.description.trim()).map((entry) => ({
        ...entry,
        taskId: entry.taskId || null,
        documentId: entry.documentId || null,
        fieldEvidenceId: entry.fieldEvidenceId || null,
      })),
    };
    try {
      if (fieldMode && !navigator.onLine) {
        throw new Error('offline');
      }
      const created = await dfrService.create(payload);
      if (submitAfter) await dfrService.submit(created.id);
      setDialogOpen(false);
      await load();
    } catch (err: any) {
      if (fieldMode) {
        const local = await enqueueOfflineDfr(payload, submitAfter);
        setRows((current) => filterDfrRows(mergeById(current, [local]), status));
        setDialogOpen(false);
        setNotice(`DFR saved offline and will sync when the device reconnects.`);
        setError(null);
      } else {
        setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save DFR');
      }
    }
  };

  const updateEntry = (index: number, patch: Partial<DfrEntryPayload>) => {
    const entries = [...(form.entries ?? [])];
    entries[index] = { ...entries[index], ...patch };
    setForm({ ...form, entries });
  };

  const appendText = (current: string | null | undefined, text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return current ?? '';
    return current?.trim() ? `${current.trim()} ${trimmed}` : trimmed;
  };

  if (!activeProjectId) {
    return <Box><PageHeader title="Daily Field Reports" /><Alert severity="info">Select a project to view DFRs.</Alert></Box>;
  }

  return (
    <Box>
      <PageHeader
        title="Daily Field Reports"
        subtitle={`${totals.reports} reports / ${totals.entries} entries`}
        actions={
          <Stack direction="row" spacing={1}>
            <IconButton onClick={load} disabled={loading} aria-label="Refresh DFRs"><Refresh /></IconButton>
            {fieldMode && <Button variant="contained" startIcon={<Add />} onClick={openCreate}>New DFR</Button>}
          </Stack>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <TextField select size="small" label="Status" value={status} onChange={(event) => setStatus(event.target.value as DfrStatus | '')} sx={{ minWidth: 180 }}>
          <MenuItem value="">All</MenuItem>
          {(['draft', 'submitted', 'approved', 'returned'] as DfrStatus[]).map((value) => <MenuItem key={value} value={value}>{labelize(value)}</MenuItem>)}
        </TextField>
      </Stack>
      <Paper sx={{ overflow: 'hidden', borderRadius: 1 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Manpower</TableCell>
              <TableCell>Weather</TableCell>
              <TableCell>Summary</TableCell>
              <TableCell align="right">{fieldMode ? 'Detail' : 'Actions'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.reportDate}</TableCell>
                <TableCell><Chip size="small" color={row.status === 'submitted' ? 'primary' : 'default'} label={labelize(row.status)} /></TableCell>
                <TableCell>{row.manpowerCount}</TableCell>
                <TableCell>{row.weather || '-'}</TableCell>
                <TableCell>{row.workSummary || '-'}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <IconButton aria-label="View DFR" onClick={() => setSelected(row)}><Visibility /></IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth fullScreen={fieldMode}>
        <DialogTitle>New Daily Field Report</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Report Date" InputLabelProps={{ shrink: true }} value={form.reportDate} onChange={(event) => setForm({ ...form, reportDate: event.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Weather" value={form.weather ?? ''} onChange={(event) => setForm({ ...form, weather: event.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Temperature C" value={form.temperatureC ?? ''} onChange={(event) => setForm({ ...form, temperatureC: event.target.value === '' ? null : Number(event.target.value) })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Manpower Count" value={form.manpowerCount ?? 0} onChange={(event) => setForm({ ...form, manpowerCount: Number(event.target.value) })} /></Grid>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <TextField fullWidth multiline minRows={2} label="Work Summary" value={form.workSummary ?? ''} onChange={(event) => setForm({ ...form, workSummary: event.target.value })} />
                {fieldMode && <VoiceInput onText={(text) => setForm((current) => ({ ...current, workSummary: appendText(current.workSummary, text) }))} />}
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <TextField fullWidth multiline minRows={2} label="Delays / Constraints" value={form.delaysConstraints ?? ''} onChange={(event) => setForm({ ...form, delaysConstraints: event.target.value })} />
                {fieldMode && <VoiceInput onText={(text) => setForm((current) => ({ ...current, delaysConstraints: appendText(current.delaysConstraints, text) }))} />}
              </Stack>
            </Grid>
            {(form.entries ?? []).map((entry, index) => (
              <Grid item xs={12} key={index}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={4}><TextField fullWidth select label="Entry Type" value={entry.entryType} onChange={(event) => updateEntry(index, { entryType: event.target.value as DfrEntryType })}>{entryTypes.map((value) => <MenuItem key={value} value={value}>{labelize(value)}</MenuItem>)}</TextField></Grid>
                    <Grid item xs={12} sm={8}>
                      <Stack spacing={1}>
                        <TextField fullWidth label="Description" value={entry.description} onChange={(event) => updateEntry(index, { description: event.target.value })} />
                        {fieldMode && <VoiceInput onText={(text) => updateEntry(index, { description: appendText(entry.description, text) })} />}
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth label="Task ID" value={entry.taskId ?? ''} onChange={(event) => updateEntry(index, { taskId: event.target.value })} /></Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth label="Document ID" value={entry.documentId ?? ''} onChange={(event) => updateEntry(index, { documentId: event.target.value })} /></Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth label="Field Evidence ID" value={entry.fieldEvidenceId ?? ''} onChange={(event) => updateEntry(index, { fieldEvidenceId: event.target.value })} /></Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
            <Grid item xs={12}><Button onClick={() => setForm({ ...form, entries: [...(form.entries ?? []), newEntry()] })}>Add Entry</Button></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => save(false)} disabled={!form.projectId || !form.reportDate}>Save Draft</Button>
          <Button variant="contained" startIcon={<Send />} onClick={() => save(true)} disabled={!form.projectId || !form.reportDate}>Submit</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>{selected?.reportDate} DFR</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={selected?.status ?? ''} color={selected?.status === 'submitted' ? 'primary' : 'default'} />
              <Chip label={`Manpower ${selected?.manpowerCount ?? 0}`} />
              {selected?.weather && <Chip label={selected.weather} />}
            </Stack>
            <Box>{selected?.workSummary || '-'}</Box>
            {selected?.delaysConstraints && <Alert severity="warning">{selected.delaysConstraints}</Alert>}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Evidence</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(selected?.entries ?? []).map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{labelize(entry.entryType)}</TableCell>
                    <TableCell>{entry.description}</TableCell>
                    <TableCell>{entry.fieldEvidenceId || entry.documentId || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function filterDfrRows(rows: DailyFieldReport[], status: DfrStatus | ''): DailyFieldReport[] {
  return rows
    .filter((row) => !status || row.status === status)
    .sort((a, b) => `${b.reportDate}${b.createdAt}`.localeCompare(`${a.reportDate}${a.createdAt}`));
}
